import type { components } from "./generated/schema";
import { ApiProblem, type ProblemDetails } from "./problemDetails";

export type Schema = components["schemas"];
export type ApiMeta = Schema["ApiMeta"];
export type ApiEnvelope<T> = { data: T; meta: ApiMeta | null; traceId: string };
export type Etagged<T> = ApiEnvelope<T> & { etag: string | null };

// Storyboard editing needs a UI-specific draft shape (autosave, local scene
// ordering and diff state). Transport contracts remain generated from OpenAPI;
// this view model is deliberately mapped at the client boundary.
export type StoryboardRevisionStatus = "Working" | "InReview" | "AwaitingReview" | "ChangesRequested" | "Approved" | "Rejected" | "Superseded";
export type StoryboardReviewDecision = "Approve" | "Reject" | "RequestChanges";

export interface StoryboardEvidenceReference {
  claimId: string | null;
  claimRevisionId: string | null;
  researchSourceVersionId: string | null;
}

export interface StoryboardNarrationSegment {
  id: string;
  order: number;
  text: string;
  kind: "Claim" | "NonMaterial";
  evidence: StoryboardEvidenceReference[];
}

export interface StoryboardScene {
  id: string;
  order: number;
  visualIntent: string;
  motionIntent: string;
  durationSeconds: number;
  renderOverride: string | null;
  videoInputMode: "ImageToVideo" | "TextToVideo";
  narrationSegments: StoryboardNarrationSegment[];
}

export interface StoryboardRevision {
  id: string;
  campaignId: string;
  outputId: string;
  number: number;
  status: StoryboardRevisionStatus;
  version: number;
  inputFingerprint: string;
  title: string;
  description: string;
  hook: string;
  callToAction: string;
  targetDurationSeconds: number;
  estimatedNarrationDurationSeconds: number;
  costDeltaUsd: number | string | null;
  scenes: StoryboardScene[];
  createdAt: string;
  updatedAt: string;
}

export interface StoryboardRevisionSummary {
  id: string;
  number: number;
  status: StoryboardRevisionStatus;
  version: number;
  createdAt: string;
  inputFingerprint: string;
}

export interface StoryboardComment {
  id: string;
  revisionId: string;
  sceneId: string | null;
  body: string;
  createdBy: string;
  createdAt: string;
}

export interface StoryboardDiff {
  fromRevisionId: string;
  toRevisionId: string;
  summary: string[];
  addedSceneIds: string[];
  removedSceneIds: string[];
  changedSceneIds: string[];
}

export interface StoryboardRevisionPatch {
  title: string;
  description: string;
  hook: string;
  callToAction: string;
  scenes: StoryboardScene[];
}

export type RunCampaignRequest = Schema["RunCampaignRequest"];

export interface StoryboardReviewRequest {
  decision: StoryboardReviewDecision;
  reason: string;
  confirm: true;
}

export interface StoryboardRegenerationPreviewRequest {
  target: "Output" | "Scene";
  sceneId: string | null;
  reason: string;
}

export interface StoryboardRegenerationPreview {
  id: string;
  fingerprint: string;
  invalidatesApproval: boolean;
  estimatedCostDeltaUsd: number | string | null;
  expiresAt: string;
}

// The quality-review transport is generated from the v1 OpenAPI contract. The
// UI reads only this safe projection, which intentionally omits FFmpeg args,
// local paths, source bytes and provider payloads.
export type QualityFindingView = Schema["QualityFindingResponse"];
export type QualityReportView = Schema["QualityReportResponse"];
export type OutputRenderView = Schema["OutputRenderResponse"];
export type SecurityEventView = Schema["SecurityEventResponse"];
export type YouTubeUploadView = Schema["YouTubeUploadResponse"];

// The committed OpenAPI schema is regenerated with this slice. Keep these
// operator view models at the client boundary, just as the storyboard models
// above do: they intentionally omit OAuth tokens, provider payloads, prompt
// bodies and media bytes while preserving the generated wire contract.
export type MetadataPreflightStatus = "Pending" | "Running" | "Passed" | "Warning" | "Blocked" | "Stale" | "Failed";
export type MetadataCategoryCatalogStatus = "Available" | "Failed";
export type MetadataSuggestionGenerationStatus = "Queued" | "Running" | "Succeeded" | "Failed" | "Canceled";
export type MetadataVersionSource = "Editor" | "Suggestion";
export type MetadataApprovalDecision = "Approved" | "Rejected" | "ApprovedByPolicy";

export interface MetadataCategoryView {
  id: string;
  title: string;
  assignable: boolean;
}

export interface MetadataCategoryCatalogView {
  id: string;
  youtubeConnectionId: string;
  countryCode: string;
  status: MetadataCategoryCatalogStatus;
  catalogFingerprint: string;
  resultCode: string | null;
  fetchedAt: string;
  expiresAt: string;
  isFresh: boolean;
}

export interface MetadataSuggestionGenerationView {
  id: string;
  renderRevisionId: string;
  status: MetadataSuggestionGenerationStatus;
  attemptCount: number;
  resultCode: string | null;
  errorCode: string | null;
  provider: string | null;
  model: string | null;
  createdAt: string;
  completedAt: string | null;
  version: number;
}

export interface MetadataSuggestionView {
  id: string;
  generationId: string;
  candidateIndex: number;
  title: string;
  description: string;
  tags: string[];
  categoryId: string | null;
  categoryTitle: string | null;
  language: string | null;
  playlistReference: string | null;
  createdAt: string;
}

export interface MetadataPackageVersionView {
  id: string;
  number: number;
  source: MetadataVersionSource;
  sourceSuggestionId: string | null;
  title: string;
  description: string;
  tags: string[];
  categoryId: string;
  categoryTitle: string;
  language: string;
  playlistReference: string | null;
  renderRevisionId: string;
  captionRevisionId: string | null;
  captionWebVttAssetId: string | null;
  captionSrtAssetId: string | null;
  thumbnailSelectionId: string | null;
  thumbnailAssetId: string | null;
  relatedMainOutputId: string | null;
  channelRevisionId: string;
  metadataTemplateHash: string;
  promptTemplateVersionId: string | null;
  promptContentHash: string | null;
  isMadeForKids: boolean;
  containsSyntheticMedia: boolean;
  initialVisibility: "Private";
  contentFingerprint: string;
  createdAt: string;
}

export interface MetadataPreflightFindingView {
  id: string;
  runId: string;
  kind: "YouTubeConstraint" | "CategoryCatalog" | "MediaPin" | "Attribution" | "ChannelPolicy" | "RiskyClaim" | "Disclosure" | "MainOutputLinkage";
  severity: "Info" | "Warning" | "Blocked";
  code: string;
  affectedField: string | null;
  claimRevisionId: string | null;
  assetId: string | null;
  summary: string | null;
}

export interface MetadataPreflightView {
  id: string;
  packageVersionId: string;
  categoryCatalogId: string | null;
  status: MetadataPreflightStatus;
  inputFingerprint: string;
  policyFingerprint: string;
  categoryCatalogFingerprint: string | null;
  resultCode: string | null;
  version: number;
  createdAt: string;
  completedAt: string | null;
}

export interface MetadataApprovalView {
  id: string;
  packageVersionId: string;
  preflightRunId: string;
  decision: MetadataApprovalDecision;
  actorId: string | null;
  reason: string;
  createdAt: string;
}

export interface MetadataPackageSummary {
  id: string;
  campaignId: string;
  outputId: string;
  channelId: string;
  currentVersionId: string | null;
  version: number;
  updatedAt: string;
}

export interface MetadataPackageDetail extends MetadataPackageSummary {
  createdAt: string;
  currentVersion: MetadataPackageVersionView | null;
  versions: MetadataPackageVersionView[];
  generations: MetadataSuggestionGenerationView[];
  suggestions: MetadataSuggestionView[];
  currentPreflight: MetadataPreflightView | null;
  preflightRuns: MetadataPreflightView[];
  preflightFindings: MetadataPreflightFindingView[];
  approvals: MetadataApprovalView[];
  categoryCatalog: MetadataCategoryCatalogView | null;
  categories: MetadataCategoryView[];
  campaignVersion: number;
  requiresHumanApproval: boolean;
}

export interface MetadataPackageVersionInput {
  expectedCampaignVersion: number;
  title: string;
  description: string;
  tags: string[];
  categoryId: string;
  language: string;
  playlistReference: string | null;
  sourceSuggestionId: string | null;
  confirm: true;
}

export interface MetadataPreflightRequest {
  confirm: true;
}

export interface MetadataSuggestionRefreshRequest {
  expectedCampaignVersion: number;
  confirm: true;
}

export interface MetadataApprovalRequest {
  reason: string;
  confirm: true;
}

// Privacy transports intentionally use a small local view model at the client
// boundary. The authentication proof is opaque and short lived; callers must
// never put it in browser storage or a durable draft.
export type PrivacyExportRequestStatus = "Queued" | "Building" | "Available" | "Expired" | "Failed" | "Canceled";
export type PrivacyErasureRequestStatus = "Requested" | "Processing" | "Completed" | "Rejected" | "Canceled" | "Failed";
export type PrivacyErasureBlockerCode = "SoleWorkspaceOwner" | "PlatformAdministrator";

export interface PrivacyExportRequestView {
  id: string;
  status: PrivacyExportRequestStatus;
  requestedAt: string;
  availableAt: string | null;
  expiresAt: string | null;
  resultCode: string | null;
  version: number;
}

export interface PrivacyErasureRequestView {
  id: string;
  status: PrivacyErasureRequestStatus;
  requestedAt: string;
  completedAt: string | null;
  resultCode: string | null;
  version: number;
}

export interface PrivacySelfServiceView {
  version: number;
  erasureEligibility: {
    canRequest: boolean;
    blockers: PrivacyErasureBlockerCode[];
  };
  latestExport: PrivacyExportRequestView | null;
  latestErasure: PrivacyErasureRequestView | null;
}

export interface PrivacyReauthenticationProofView {
  proof: string;
  expiresAt: string;
}

type StoryboardWireResponse = {
  revision: { id: string; campaignId: string; number: number; status: StoryboardRevisionStatus; snapshotHash: string; createdAt: string; updatedAt: string };
  output: { logicalOutputId: string; title: string; description: string; hook: string; callToAction: string; scenes: Array<{ logicalSceneId: string; order: number; visualIntent: string; motionIntent: string; durationSeconds: number; renderOverride: string | null; videoInputMode: "ImageToVideo" | "TextToVideo"; narration: Array<{ logicalSegmentId: string; order: number; text: string; kind: "Claim" | "NonMaterial"; evidence: StoryboardEvidenceReference[] }> }> };
  revisionVersion: number;
  estimatedCostUsd: number | string;
};

type StoryboardRevisionWire = { id: string; campaignId: string; number: number; status: StoryboardRevisionStatus; snapshotHash: string; createdAt: string; updatedAt: string };

function mapStoryboardResponse(response: Etagged<StoryboardWireResponse>): Etagged<StoryboardRevision> {
  const { revision, output, revisionVersion, estimatedCostUsd } = response.data;
  const scenes = output.scenes.map(scene => ({
    id: scene.logicalSceneId, order: scene.order, visualIntent: scene.visualIntent, motionIntent: scene.motionIntent,
    durationSeconds: scene.durationSeconds, renderOverride: scene.renderOverride, videoInputMode: scene.videoInputMode,
    narrationSegments: scene.narration.map(segment => ({ id: segment.logicalSegmentId, order: segment.order, text: segment.text, kind: segment.kind, evidence: segment.evidence }))
  }));
  const duration = scenes.reduce((total, scene) => total + scene.durationSeconds, 0);
  return { ...response, data: { id: revision.id, campaignId: revision.campaignId, outputId: output.logicalOutputId, number: revision.number,
    status: revision.status, version: revisionVersion, inputFingerprint: revision.snapshotHash, title: output.title, description: output.description,
    hook: output.hook, callToAction: output.callToAction, targetDurationSeconds: duration, estimatedNarrationDurationSeconds: duration,
    costDeltaUsd: estimatedCostUsd, scenes, createdAt: revision.createdAt, updatedAt: revision.updatedAt } };
}

function storyboardPatch(values: StoryboardRevisionPatch) {
  return { title: values.title, description: values.description, hook: values.hook,
    callToAction: values.callToAction, scenes: values.scenes.map(scene => ({ logicalSceneId: scene.id, order: scene.order,
      visualIntent: scene.visualIntent, motionIntent: scene.motionIntent, durationSeconds: scene.durationSeconds, renderOverride: scene.renderOverride, videoInputMode: scene.videoInputMode,
      narration: scene.narrationSegments.map(segment => ({ logicalSegmentId: segment.id, order: segment.order, text: segment.text, kind: segment.kind, evidence: segment.evidence })) })) };
}

type RequestOptions = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown;
  headers?: HeadersInit;
  csrf?: boolean;
  idempotency?: boolean;
  etag?: string;
};

let csrfToken: string | null = null;

function mutation(method: string | undefined): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes((method ?? "GET").toUpperCase());
}

function idempotencyKey(): string {
  return crypto.randomUUID();
}

async function parseProblem(response: Response): Promise<ApiProblem> {
  const body = await response.json().catch(() => ({})) as ProblemDetails;
  return new ApiProblem(body, response.status);
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<Etagged<T>> {
  const method = options.method ?? "GET";
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (options.etag) headers.set("If-Match", options.etag);
  if (options.csrf ?? mutation(method)) {
    if (csrfToken) headers.set("X-CSRF-TOKEN", csrfToken);
  }
  if (options.idempotency ?? mutation(method)) {
    headers.set("Idempotency-Key", idempotencyKey());
  }

  const response = await fetch(`/api/v1${path}`, {
    ...options,
    method,
    headers,
    credentials: "same-origin",
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  if (!response.ok) {
    const problem = await parseProblem(response);
    if (response.status === 401 && path !== "/session" && path !== "/session/login") {
      window.dispatchEvent(new Event("contentforge:session-expired"));
    }
    throw problem;
  }
  if (response.status === 204) {
    return { data: undefined as T, meta: null, traceId: "", etag: response.headers.get("ETag") };
  }
  const envelope = await response.json() as ApiEnvelope<T>;
  return { ...envelope, etag: response.headers.get("ETag") };
}

async function requestForm<T>(path: string, form: FormData, etag?: string): Promise<Etagged<T>> {
  const headers = new Headers({ Accept: "application/json", "Idempotency-Key": idempotencyKey() });
  if (csrfToken) headers.set("X-CSRF-TOKEN", csrfToken);
  if (etag) headers.set("If-Match", etag);
  const response = await fetch(`/api/v1${path}`, { method: "POST", headers, credentials: "same-origin", body: form });
  if (!response.ok) throw await parseProblem(response);
  const envelope = await response.json() as ApiEnvelope<T>;
  return { ...envelope, etag: response.headers.get("ETag") };
}

async function download(path: string): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await fetch(`/api/v1${path}`, { credentials: "same-origin" });
  if (!response.ok) throw await parseProblem(response);
  const disposition = response.headers.get("Content-Disposition");
  const fileName = disposition?.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)?.slice(1).find(Boolean) ?? null;
  return { blob: await response.blob(), fileName: fileName ? decodeURIComponent(fileName) : null };
}

export const api = {
  setCsrfToken(token: string | null): void {
    csrfToken = token;
  },
  clearSessionState(): void {
    csrfToken = null;
  },
  login(values: Schema["LoginRequest"]) {
    return request<Schema["LoginResponse"]>("/session/login", { method: "POST", body: values, csrf: false, idempotency: false });
  },
  logout() {
    return request<void>("/session/logout", { method: "POST" });
  },
  session() {
    return request<Schema["SessionResponse"]>("/session");
  },
  async refreshCsrf(): Promise<string> {
    const response = await request<Schema["CsrfTokenResponse"]>("/session/csrf");
    csrfToken = response.data.token;
    return response.data.token;
  },
  listWorkspaces() {
    return request<Schema["WorkspaceMembershipResponse"][]>("/workspaces");
  },
  createWorkspace(values: Schema["CreateWorkspaceRequest"]) {
    return request<Schema["WorkspaceMembershipResponse"]>("/workspaces", { method: "POST", body: values });
  },
  getWorkspace(id: string) {
    return request<Schema["WorkspaceResponse"]>(`/workspaces/${id}`);
  },
  updateWorkspace(id: string, values: Schema["CreateWorkspaceRequest"]) {
    return request<Schema["WorkspaceResponse"]>(`/workspaces/${id}`, { method: "PATCH", body: values });
  },
  setActiveWorkspace(workspaceId: string) {
    return request<Schema["ActiveWorkspaceResponse"]>("/session/active-workspace", { method: "POST", body: { workspaceId } });
  },
  listMembers(workspaceId: string) {
    return request<Schema["WorkspaceMemberResponse"][]>(`/workspaces/${workspaceId}/members`);
  },
  inviteMember(workspaceId: string, values: Schema["InviteMemberRequest"]) {
    return request<Schema["WorkspaceInvitationResponse"]>(`/workspaces/${workspaceId}/members`, { method: "POST", body: values });
  },
  updateMember(workspaceId: string, memberId: string, values: Schema["UpdateMembershipRequest"]) {
    return request<Schema["WorkspaceMemberResponse"]>(`/workspaces/${workspaceId}/members/${memberId}`, { method: "PATCH", body: values });
  },
  disableMember(workspaceId: string, memberId: string) {
    return request<Schema["WorkspaceMemberResponse"]>(`/workspaces/${workspaceId}/members/${memberId}`, { method: "DELETE" });
  },
  acceptInvitation(token: string, values: Schema["AcceptInvitationRequest"]) {
    return request<Schema["InvitationAcceptanceResponse"]>(`/invitations/${encodeURIComponent(token)}/accept`, { method: "POST", body: values, csrf: false, idempotency: false });
  },
  health() {
    return request<Schema["SystemHealthResponse"]>("/health");
  },
  listModels() {
    return request<Schema["ModelResponse"][]>("/provider-models");
  },
  listProviderCatalog(cursor?: string, capability?: Schema["ModelCapability"]) {
    const query = new URLSearchParams();
    if (cursor) query.set("cursor", cursor);
    if (capability) query.set("capability", capability);
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["ProviderModelCatalogResponse"][]>(`/providers${suffix}`);
  },
  getProviderCatalogModel(id: string) {
    return request<Schema["ProviderModelCatalogResponse"]>(`/providers/models/${id}`);
  },
  setProviderModelActivation(id: string, enabled: boolean, etag: string) {
    return request<Schema["ProviderModelCatalogResponse"]>(`/providers/models/${id}/activation`, { method: "PATCH", body: { enabled }, etag });
  },
  listProviderConnections() {
    return request<Schema["ProviderConnectionResponse"][]>("/provider-connections");
  },
  createProviderConnection(values: Schema["CreateProviderConnectionRequest"]) {
    return request<Schema["ProviderConnectionResponse"]>("/provider-connections", { method: "POST", body: values });
  },
  updateProviderConnection(id: string, values: Schema["UpdateProviderConnectionRequest"], etag: string) {
    return request<Schema["ProviderConnectionResponse"]>(`/provider-connections/${id}`, { method: "PATCH", body: values, etag });
  },
  verifyProviderConnection(id: string, etag: string) {
    return request<Schema["ProviderConnectionResponse"]>(`/provider-connections/${id}/verify`, { method: "POST", etag });
  },
  revokeProviderConnection(id: string, etag: string) {
    return request<Schema["ProviderConnectionResponse"]>(`/provider-connections/${id}`, { method: "DELETE", etag });
  },
  listYouTubeConnections(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["YouTubeConnectionResponse"][]>(`/youtube/connections${suffix}`);
  },
  startYouTubeOAuth(values: Schema["StartYouTubeOAuthRequest"]) {
    return request<Schema["YouTubeOAuthStartResponse"]>("/youtube/connections/start", { method: "POST", body: values });
  },
  confirmYouTubeConnectionBinding(id: string, etag: string) {
    return request<Schema["YouTubeConnectionResponse"]>(`/youtube/connections/${id}/confirm-binding`, { method: "POST", body: { confirm: true }, etag });
  },
  verifyYouTubeConnection(id: string, etag: string) {
    return request<Schema["YouTubeConnectionResponse"]>(`/youtube/connections/${id}/verify`, { method: "POST", etag });
  },
  revokeYouTubeConnection(id: string, etag: string) {
    return request<Schema["YouTubeConnectionResponse"]>(`/youtube/connections/${id}/revoke`, { method: "POST", body: { confirm: true }, etag });
  },
  listProviderExecutionCircuits(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ProviderExecutionCircuitResponse"][]>(`/provider-execution/circuits${suffix}`);
  },
  resetProviderExecutionCircuit(id: string, etag: string) {
    return request<Schema["ProviderExecutionCircuitResponse"]>(`/provider-execution/circuits/${encodeURIComponent(id)}/reset`, { method: "POST", etag });
  },
  listPromptTemplates(channelId?: string, cursor?: string) {
    const query = new URLSearchParams();
    if (channelId) query.set("channelId", channelId);
    if (cursor) query.set("cursor", cursor);
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["PromptTemplateResponse"][]>(`/prompt-templates${suffix}`);
  },
  getPromptTemplate(id: string) {
    return request<Schema["PromptTemplateResponse"]>(`/prompt-templates/${id}`);
  },
  getPromptTemplateInheritancePreview(channelId: string, useCase: Schema["PromptUseCase"]) {
    return request<Schema["PromptTemplateInheritancePreviewResponse"]>(`/prompt-templates/inheritance-preview?${new URLSearchParams({ channelId, useCase }).toString()}`);
  },
  createPromptTemplate(values: Schema["CreatePromptTemplateRequest"]) {
    return request<Schema["PromptTemplateResponse"]>("/prompt-templates", { method: "POST", body: values });
  },
  createPromptTemplateDraft(id: string, values: Schema["PromptTemplateDraftRequest"], etag: string) {
    return request<Schema["PromptTemplateResponse"]>(`/prompt-templates/${id}/drafts`, { method: "POST", body: values, etag });
  },
  rebasePromptTemplate(id: string, values: Schema["PromptTemplateRebaseRequest"], etag: string) {
    return request<Schema["PromptTemplateResponse"]>(`/prompt-templates/${id}/rebase`, { method: "POST", body: values, etag });
  },
  evaluatePromptTemplate(id: string, versionId: string) {
    return request<Schema["PromptEvaluationResponse"]>(`/prompt-templates/${id}/versions/${versionId}/evaluate`, { method: "POST" });
  },
  getPromptEvaluation(runId: string) {
    return request<Schema["PromptEvaluationResponse"]>(`/prompt-templates/evaluations/${runId}`);
  },
  getPromptTemplateDiff(id: string, versionId: string, againstVersionId: string) {
    return request<Schema["PromptTemplateDiffResponse"]>(`/prompt-templates/${id}/versions/${versionId}/diff/${againstVersionId}`);
  },
  listPromptTemplateCampaignUsage(id: string, versionId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["PromptTemplateCampaignUsageResponse"][]>(`/prompt-templates/${id}/versions/${versionId}/campaigns${suffix}`);
  },
  publishPromptTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["PromptTemplateResponse"]>(`/prompt-templates/${id}/versions/${versionId}/publish`, { method: "POST", etag });
  },
  activatePromptTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["PromptTemplateResponse"]>(`/prompt-templates/${id}/versions/${versionId}/activate`, { method: "POST", etag });
  },
  rollbackPromptTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["PromptTemplateResponse"]>(`/prompt-templates/${id}/versions/${versionId}/rollback`, { method: "POST", etag });
  },
  deprecatePromptTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["PromptTemplateResponse"]>(`/prompt-templates/${id}/versions/${versionId}/deprecate`, { method: "POST", etag });
  },
  listPricing(cursor?: string, includeHistory = false) {
    const query = new URLSearchParams();
    if (cursor) query.set("cursor", cursor);
    if (includeHistory) query.set("includeHistory", "true");
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["PriceCatalogItemResponse"][]>(`/pricing${suffix}`);
  },
  importPricing(values: Schema["PriceImportRequest"]) {
    return request<Schema["PriceImportResponse"]>("/pricing/import", { method: "POST", body: values });
  },
  createPricingOverride(input: Schema["PriceOverrideInput"]) {
    return request<Schema["PriceVersionResponse"]>("/pricing/overrides", { method: "POST", body: { input } });
  },
  createCostEstimate(values: Schema["EstimateCampaignCommand"]) {
    return request<Schema["CostEstimateResponse"]>("/cost-estimates", { method: "POST", body: values, idempotency: false });
  },
  listBudgets(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["BudgetPolicyItemResponse"][]>(`/budgets${suffix}`);
  },
  createBudget(values: Schema["BudgetPolicyRequest"]) {
    return request<Schema["BudgetPolicyItemResponse"]>("/budgets", { method: "POST", body: values });
  },
  updateBudget(id: string, values: Schema["BudgetPolicyRequest"], etag: string) {
    return request<Schema["BudgetPolicyItemResponse"]>(`/budgets/${id}`, { method: "PATCH", body: values, etag });
  },
  disableBudget(id: string, etag: string) {
    return request<Schema["BudgetPolicyItemResponse"]>(`/budgets/${id}/disable`, { method: "POST", etag });
  },
  listCostLedger(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["LedgerEntryResponse"][]>(`/cost-ledger${suffix}`);
  },
  listCostReservations(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ReservationResponse"][]>(`/cost-reservations${suffix}`);
  },
  getPaidDispatchRollout() {
    return request<Schema["PaidDispatchRolloutResponse"]>("/paid-dispatch-rollout");
  },
  setPaidDispatchRollout(values: Schema["PaidDispatchRolloutRequest"], etag: string) {
    return request<Schema["PaidDispatchRolloutResponse"]>("/paid-dispatch-rollout", { method: "PUT", body: values, etag });
  },
  addCostAdjustment(values: Schema["CostAdjustmentRequest"]) {
    return request<Schema["LedgerEntryResponse"]>("/cost-ledger/adjustments", { method: "POST", body: values });
  },
  resolveCostReservation(id: string, values: Schema["ResolveReservationRequest"]) {
    return request<Schema["ReservationResponse"]>(`/cost-reservations/${id}/resolve`, { method: "POST", body: values });
  },
  listReconciliations(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ReconciliationRunResponse"][]>(`/reconciliations${suffix}`);
  },
  getReconciliation(id: string) {
    return request<Schema["ReconciliationDetailsResponse"]>(`/reconciliations/${id}`);
  },
  startReconciliation(values: Schema["StartReconciliationRequest"]) {
    return request<Schema["ReconciliationRunResponse"]>("/reconciliations", { method: "POST", body: values });
  },
  listChannels(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ChannelResponse"][]>(`/channels${suffix}`);
  },
  getChannel(id: string) {
    return request<Schema["ChannelResponse"]>(`/channels/${id}`);
  },
  createChannel(values: Schema["CreateChannelRequest"]) {
    return request<Schema["ChannelResponse"]>("/channels", { method: "POST", body: values });
  },
  createChannelDraft(id: string, values: Schema["ChannelDraftRequest"], etag: string) {
    return request<Schema["ChannelResponse"]>(`/channels/${id}`, { method: "PATCH", body: values, etag });
  },
  cloneChannel(id: string, values: Schema["CloneChannelRequest"], etag: string) {
    return request<Schema["ChannelResponse"]>(`/channels/${id}/clone`, { method: "POST", body: values, etag });
  },
  listChannelRevisions(id: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ChannelRevisionResponse"][]>(`/channels/${id}/revisions${suffix}`);
  },
  activateChannelRevision(id: string, revisionId: string, etag: string) {
    return request<Schema["ChannelResponse"]>(`/channels/${id}/activate-revision`, { method: "POST", body: { revisionId }, etag });
  },
  archiveChannel(id: string, etag: string) {
    return request<Schema["ChannelResponse"]>(`/channels/${id}/archive`, { method: "POST", body: { confirm: true }, etag });
  },
  listVoiceConsents() {
    return request<Schema["VoiceConsentResponse"][]>("/voice-consents");
  },
  createVoiceConsent(values: Schema["VoiceConsentRequest"]) {
    return request<Schema["VoiceConsentResponse"]>("/voice-consents", { method: "POST", body: values });
  },
  revokeVoiceConsent(id: string, etag: string) {
    return request<Schema["VoiceConsentResponse"]>(`/voice-consents/${id}/revoke`, { method: "POST", body: { confirm: true }, etag });
  },
  listCustomVoices() {
    return request<Schema["CustomVoiceResponse"][]>("/custom-voices");
  },
  createCustomVoice(values: Schema["CustomVoiceRequest"]) {
    return request<Schema["CustomVoiceResponse"]>("/custom-voices", { method: "POST", body: values });
  },
  revokeCustomVoice(id: string, etag: string) {
    return request<Schema["CustomVoiceResponse"]>(`/custom-voices/${id}/revoke`, { method: "POST", body: { confirm: true }, etag });
  },
  listNarrationAudioRevisions(segmentId: string) {
    return request<Schema["HumanNarrationVerificationResponse"][]>(`/narration-segments/${segmentId}/audio-revisions`);
  },
  uploadHumanNarration(segmentId: string, file: File, evidenceAssetId: string, confirm: boolean) {
    const form = new FormData();
    form.append("file", file); form.append("evidenceAssetId", evidenceAssetId); form.append("confirm", String(confirm));
    return requestForm<Schema["HumanNarrationVerificationResponse"]>(`/narration-segments/${segmentId}/human-audio`, form);
  },
  createNarrationSegmentGenerationPreview(segmentId: string, etag: string) {
    return request<Schema["NarrationSegmentGenerationPreviewResponse"]>(`/narration-segments/${segmentId}/generation-previews`, { method: "POST", body: {}, etag });
  },
  startNarrationSegmentGeneration(segmentId: string, values: Schema["NarrationSegmentGenerationStartRequest"], etag: string) {
    return request<Schema["NarrationSegmentGenerationStartResponse"]>(`/narration-segments/${segmentId}/generations`, { method: "POST", body: values, etag });
  },
  getAsset(id: string) {
    return request<Schema["AssetResponse"]>(`/assets/${id}`);
  },
  listAssets(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["AssetResponse"][]>(`/assets${suffix}`);
  },
  getAssetLineage(id: string) {
    return request<Schema["AssetLineageResponse"]>(`/assets/${id}/lineage`);
  },
  uploadAsset(file: File, values: { sourceKind: Schema["AssetSourceKind"]; rightsClassification: Schema["AssetRightsClassification"]; attributionRequired: boolean; attributionText?: string; territories?: string; expiresAt?: string; evidenceAssetId?: string; reason?: string }) {
    const form = new FormData();
    form.append("file", file);
    form.append("sourceKind", values.sourceKind);
    form.append("rightsClassification", values.rightsClassification);
    form.append("attributionRequired", String(values.attributionRequired));
    if (values.attributionText) form.append("attributionText", values.attributionText);
    if (values.territories) form.append("territories", values.territories);
    if (values.expiresAt) form.append("expiresAt", values.expiresAt);
    if (values.evidenceAssetId) form.append("evidenceAssetId", values.evidenceAssetId);
    if (values.reason) form.append("reason", values.reason);
    return requestForm<Schema["AssetResponse"]>("/assets", form);
  },
  reviseAssetRights(id: string, values: Schema["RightsRevisionRequest"], etag: string) {
    return request<Schema["AssetResponse"]>(`/assets/${id}/rights-revisions`, { method: "POST", body: values, etag });
  },
  downloadAsset(id: string, inline = false) {
    return download(`/assets/${id}/content?${new URLSearchParams({ download: String(!inline) }).toString()}`);
  },
  listOutputScenes(outputId: string) {
    return request<Schema["StoryboardSceneResponse"][]>(`/outputs/${outputId}/scenes`);
  },
  getOutputCaptions(outputId: string) {
    return request<Schema["CaptionTrackResponse"]>(`/outputs/${outputId}/captions`);
  },
  listOutputRenders(outputId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<OutputRenderView[]>(`/outputs/${outputId}/renders${suffix}`);
  },
  startOutputRender(outputId: string, etag: string) {
    return request<{ outputId: string; operationId: string }>(`/outputs/${outputId}/renders`, { method: "POST", body: { confirm: true }, etag });
  },
  getOutputRender(renderId: string) {
    return request<OutputRenderView>(`/renders/${renderId}`);
  },
  getOutputQualityReport(renderId: string) {
    return request<QualityReportView>(`/renders/${renderId}/quality-report`);
  },
  overrideOutputQualityWarning(renderId: string, values: { reason: string; confirm: true }, etag: string) {
    return request<OutputRenderView>(`/renders/${renderId}/quality-overrides`, { method: "POST", body: values, etag });
  },
  approveFinalMedia(renderId: string, values: { reason: string; fallbackDisclosureConfirmed: boolean; confirm: true }, etag: string) {
    return request<OutputRenderView>(`/renders/${renderId}/approve`, { method: "POST", body: values, etag });
  },
  rejectFinalMedia(renderId: string, values: { reason: string; confirm: true }, etag: string) {
    return request<OutputRenderView>(`/renders/${renderId}/reject`, { method: "POST", body: values, etag });
  },
  listOutputMetadataPackages(outputId: string) {
    return request<MetadataPackageSummary[]>(`/outputs/${outputId}/metadata-packages`);
  },
  getMetadataPackage(id: string) {
    return request<MetadataPackageDetail>(`/metadata-packages/${id}`);
  },
  createMetadataPackage(outputId: string, values: MetadataPackageVersionInput, etag: string) {
    return request<MetadataPackageDetail>(`/outputs/${outputId}/metadata-packages`, { method: "POST", body: values, etag });
  },
  refreshMetadataPackageSuggestions(outputId: string, values: MetadataSuggestionRefreshRequest, etag: string) {
    return request<MetadataPackageDetail>(`/outputs/${outputId}/metadata-packages/refresh`, { method: "POST", body: values, etag });
  },
  preflightMetadataPackage(id: string, values: MetadataPreflightRequest, etag: string) {
    return request<MetadataPackageDetail>(`/metadata-packages/${id}/preflight`, { method: "POST", body: values, etag });
  },
  approveMetadataPackage(id: string, values: MetadataApprovalRequest, etag: string) {
    return request<MetadataPackageDetail>(`/metadata-packages/${id}/approval`, { method: "POST", body: values, etag });
  },
  listYouTubeUploads(outputId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<YouTubeUploadView[]>(`/outputs/${outputId}/uploads${suffix}`);
  },
  getYouTubeUpload(uploadId: string) {
    return request<YouTubeUploadView>(`/uploads/${uploadId}`);
  },
  startYouTubeUpload(outputId: string, values: Schema["StartYouTubeUploadRequest"], etag: string) {
    return request<YouTubeUploadView>(`/outputs/${outputId}/uploads`, { method: "POST", body: values, etag });
  },
  pauseYouTubeUpload(uploadId: string, values: Schema["ChangeYouTubeUploadRequest"], etag: string) {
    return request<YouTubeUploadView>(`/uploads/${uploadId}/pause`, { method: "POST", body: values, etag });
  },
  resumeYouTubeUpload(uploadId: string, values: Schema["ChangeYouTubeUploadRequest"], etag: string) {
    return request<YouTubeUploadView>(`/uploads/${uploadId}/resume`, { method: "POST", body: values, etag });
  },
  cancelYouTubeUpload(uploadId: string, values: Schema["ChangeYouTubeUploadRequest"], etag: string) {
    return request<YouTubeUploadView>(`/uploads/${uploadId}/cancel`, { method: "POST", body: values, etag });
  },
  repairYouTubeUploadAttachments(uploadId: string, values: Schema["ChangeYouTubeUploadRequest"], etag: string) {
    return request<YouTubeUploadView>(`/uploads/${uploadId}/repair-attachments`, { method: "POST", body: values, etag });
  },
  // CMP-001 keeps a reviewer-safe view facade in the compliance feature. The
  // transport boundary remains here with every other manually shaped operator
  // API, while the generated schema stays the source of wire-contract drift.
  listOutputComplianceCases(outputId: string) {
    return request<unknown[]>(`/outputs/${outputId}/compliance-cases`);
  },
  getComplianceReviewCase(caseId: string) {
    return request<unknown>(`/compliance-review-cases/${caseId}`);
  },
  createComplianceReviewCase(outputId: string, values: unknown, etag: string) {
    return request<unknown>(`/outputs/${outputId}/compliance-cases`, { method: "POST", body: values, etag });
  },
  runCompliancePreflight(caseId: string, values: unknown, etag: string) {
    return request<unknown>(`/compliance-review-cases/${caseId}/preflight`, { method: "POST", body: values, etag });
  },
  recordComplianceChecklistDecision(caseId: string, checklistItemId: string, values: unknown, etag: string) {
    return request<unknown>(`/compliance-review-cases/${caseId}/checklist/${checklistItemId}/decision`, { method: "POST", body: values, etag });
  },
  approveComplianceReviewCase(caseId: string, values: unknown, etag: string) {
    return request<unknown>(`/compliance-review-cases/${caseId}/approval`, { method: "POST", body: values, etag });
  },
  getMyPrivacy() {
    return request<PrivacySelfServiceView>("/privacy/me");
  },
  listMyPrivacyExports(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<PrivacyExportRequestView[]>(`/privacy/me/exports${suffix}`);
  },
  requestMyPrivacyExport(etag: string) {
    return request<PrivacyExportRequestView>("/privacy/me/exports", { method: "POST", body: { confirm: true }, etag });
  },
  issueMyPrivacyReauthenticationProof(credential: string, etag: string) {
    return request<PrivacyReauthenticationProofView>("/privacy/me/reauthentication", { method: "POST", body: { credential, confirm: true }, etag });
  },
  requestMyPrivacyErasure(reauthenticationProof: string, etag: string) {
    return request<PrivacyErasureRequestView>("/privacy/me/erasure", { method: "POST", body: { reauthenticationProof, confirm: true }, etag });
  },
  downloadMyPrivacyExport(exportId: string) {
    return download(`/privacy/me/exports/${exportId}/content`);
  },
  listCaptionRevisions(outputId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["CaptionRevisionResponse"][]>(`/outputs/${outputId}/captions/revisions${suffix}`);
  },
  buildOutputCaptions(outputId: string, etag?: string) {
    return request<Schema["CaptionBuildResponse"]>(`/outputs/${outputId}/caption-builds`, { method: "POST", body: {}, etag });
  },
  reviseOutputCaptions(outputId: string, values: Schema["CaptionRevisionRequest"], etag: string) {
    return request<Schema["CaptionTrackResponse"]>(`/outputs/${outputId}/caption-revisions`, { method: "POST", body: values, etag });
  },
  downloadOutputCaptions(outputId: string, format: "vtt" | "srt") {
    return download(`/outputs/${outputId}/captions/${format}`);
  },
  listAudioLibrary(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["AudioLibraryEntryResponse"][]>(`/audio-library${suffix}`);
  },
  createAudioLibraryEntry(values: Schema["AudioLibraryEntryRequest"]) {
    return request<Schema["AudioLibraryEntryResponse"]>("/audio-library", { method: "POST", body: values });
  },
  archiveAudioLibraryEntry(id: string, etag: string) {
    return request<Schema["AudioLibraryEntryResponse"]>(`/audio-library/${id}/archive`, { method: "POST", body: { confirm: true }, etag });
  },
  getOutputAudioMix(outputId: string) {
    return request<Schema["AudioMixDetailsResponse"]>(`/outputs/${outputId}/audio-mix`);
  },
  listOutputAudioMixRevisions(outputId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["AudioMixVersionResponse"][]>(`/outputs/${outputId}/audio-mix/revisions${suffix}`);
  },
  createOutputAudioMixRevision(outputId: string, values: Schema["AudioMixRevisionRequest"], etag: string) {
    return request<Schema["AudioMixDetailsResponse"]>(`/outputs/${outputId}/audio-mix/revisions`, { method: "POST", body: values, etag });
  },
  buildOutputAudioMix(outputId: string, etag: string) {
    return request<Schema["AudioMixBuildResponse"]>(`/outputs/${outputId}/audio-mixes`, { method: "POST", body: { confirm: true }, etag });
  },
  overrideOutputAudioMixWarning(outputId: string, mixId: string, values: Schema["AudioMixOverrideRequest"], etag: string) {
    return request<Schema["AudioMixDetailsResponse"]>(`/outputs/${outputId}/audio-mix/${mixId}/override`, { method: "POST", body: values, etag });
  },
  outputAudioMixPreviewUrl(outputId: string): string {
    return `/api/v1/outputs/${outputId}/audio-mix/preview`;
  },
  listCaptionStyles(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["CaptionStyleResponse"][]>(`/caption-styles${suffix}`);
  },
  getCaptionStyle(id: string) {
    return request<Schema["CaptionStyleResponse"]>(`/caption-styles/${id}`);
  },
  publishCaptionStyle(id: string, versionId: string, etag: string) {
    return request<Schema["CaptionStyleResponse"]>(`/caption-styles/${id}/versions/${versionId}/publish`, { method: "POST", etag });
  },
  deprecateCaptionStyle(id: string, versionId: string, etag: string) {
    return request<Schema["CaptionStyleResponse"]>(`/caption-styles/${id}/versions/${versionId}/deprecate`, { method: "POST", etag });
  },
  listRenderTemplates(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["RenderTemplateResponse"][]>(`/render-templates${suffix}`);
  },
  listThumbnailTemplates(cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ThumbnailTemplateResponse"][]>(`/thumbnail-templates${suffix}`);
  },
  getThumbnailTemplate(id: string) {
    return request<Schema["ThumbnailTemplateResponse"]>(`/thumbnail-templates/${id}`);
  },
  createThumbnailTemplate(values: Schema["CreateThumbnailTemplateRequest"]) {
    return request<Schema["ThumbnailTemplateResponse"]>("/thumbnail-templates", { method: "POST", body: values });
  },
  createThumbnailTemplateVersion(id: string, values: Schema["CreateThumbnailTemplateVersionRequest"], etag: string) {
    return request<Schema["ThumbnailTemplateResponse"]>(`/thumbnail-templates/${id}/versions`, { method: "POST", body: values, etag });
  },
  publishThumbnailTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["ThumbnailTemplateResponse"]>(`/thumbnail-templates/${id}/versions/${versionId}/publish`, { method: "POST", etag });
  },
  deprecateThumbnailTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["ThumbnailTemplateResponse"]>(`/thumbnail-templates/${id}/versions/${versionId}/deprecate`, { method: "POST", etag });
  },
  getRenderTemplate(id: string) {
    return request<Schema["RenderTemplateResponse"]>(`/render-templates/${id}`);
  },
  createRenderTemplate(values: Schema["CreateRenderTemplateRequest"]) {
    return request<Schema["RenderTemplateResponse"]>("/render-templates", { method: "POST", body: values });
  },
  createRenderTemplateVersion(id: string, values: Schema["CreateRenderTemplateVersionRequest"], etag: string) {
    return request<Schema["RenderTemplateResponse"]>(`/render-templates/${id}/versions`, { method: "POST", body: values, etag });
  },
  publishRenderTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["RenderTemplateResponse"]>(`/render-templates/${id}/versions/${versionId}/publish`, { method: "POST", etag });
  },
  deprecateRenderTemplate(id: string, versionId: string, etag: string) {
    return request<Schema["RenderTemplateResponse"]>(`/render-templates/${id}/versions/${versionId}/deprecate`, { method: "POST", etag });
  },
  listRenderPreviews(outputId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["RenderPreviewResponse"][]>(`/outputs/${outputId}/render-previews${suffix}`);
  },
  startRenderPreview(outputId: string, etag: string) {
    return request<Schema["RenderPreviewResponse"]>(`/outputs/${outputId}/render-previews`, { method: "POST", body: { confirm: true }, etag });
  },
  renderPreviewUrl(outputId: string, previewId: string): string {
    return `/api/v1/outputs/${outputId}/render-previews/${previewId}/content`;
  },
  listSceneCandidates(sceneId: string, cursor?: string, role?: "SceneImage" | "SceneVideo") {
    const query = new URLSearchParams();
    if (cursor) query.set("cursor", cursor);
    if (role) query.set("role", role);
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["SceneCandidateResponse"][]>(`/scenes/${sceneId}/candidates${suffix}`);
  },
  getOutputThumbnails(outputId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ThumbnailStudioResponse"]>(`/outputs/${outputId}/thumbnails${suffix}`);
  },
  previewThumbnailGeneration(outputId: string, values: Schema["ThumbnailGenerationPreviewRequest"], etag: string) {
    return request<Schema["ThumbnailGenerationPreviewResponse"]>(`/outputs/${outputId}/thumbnail-generation-previews`, { method: "POST", body: values, etag });
  },
  startThumbnailGeneration(outputId: string, values: Schema["StartThumbnailGenerationRequest"], etag: string) {
    return request<Schema["ThumbnailGenerationStartResponse"]>(`/outputs/${outputId}/thumbnail-generations`, { method: "POST", body: values, etag });
  },
  selectThumbnail(outputId: string, values: Schema["ThumbnailSelectionRequest"], etag: string) {
    return request<Schema["ThumbnailSelectionResponse"]>(`/outputs/${outputId}/thumbnail-selection`, { method: "POST", body: values, etag });
  },
  decideSceneCandidate(sceneId: string, candidateId: string, decision: "select" | "reject", etag: string) {
    return request<Schema["SceneCandidateResponse"]>(`/scenes/${sceneId}/candidate-selection`, { method: "POST", body: { candidateId, decision, confirm: true }, etag });
  },
  previewSceneImageGeneration(sceneId: string, values: Schema["ImageGenerationPreviewRequest"], etag: string) {
    return request<Schema["ImageGenerationPreviewResponse"]>(`/scenes/${sceneId}/image-generation-previews`, { method: "POST", body: values, etag });
  },
  startSceneImageGeneration(sceneId: string, values: Schema["StartImageGenerationRequest"], etag: string) {
    return request<Schema["ImageGenerationStartResponse"]>(`/scenes/${sceneId}/image-generations`, { method: "POST", body: values, etag });
  },
  previewSceneVideoGeneration(sceneId: string, values: Schema["VideoGenerationPreviewRequest"], etag: string) {
    return request<Schema["VideoGenerationPreviewResponse"]>(`/scenes/${sceneId}/video-generation-previews`, { method: "POST", body: values, etag });
  },
  startSceneVideoGeneration(sceneId: string, values: Schema["StartVideoGenerationRequest"], etag: string) {
    return request<Schema["VideoGenerationStartResponse"]>(`/scenes/${sceneId}/video-generations`, { method: "POST", body: values, etag });
  },
  listResearchSources(filters: { ideaId?: string; lifecycle?: Schema["ResearchSourceLifecycle"]; type?: Schema["ResearchSourceType"]; cursor?: string; limit?: number } = {}) {
    const query = new URLSearchParams();
    if (filters.ideaId) query.set("ideaId", filters.ideaId);
    if (filters.lifecycle) query.set("lifecycle", filters.lifecycle);
    if (filters.type) query.set("type", filters.type);
    if (filters.cursor) query.set("cursor", filters.cursor);
    if (filters.limit) query.set("limit", String(filters.limit));
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["ResearchSourceResponse"][]>(`/research-sources${suffix}`);
  },
  getResearchSource(id: string) {
    return request<Schema["ResearchSourceResponse"]>(`/research-sources/${id}`);
  },
  createResearchSource(values: Schema["CreateResearchSourceRequest"]) {
    return request<Schema["ResearchSourceResponse"]>("/research-sources", { method: "POST", body: values });
  },
  updateResearchSource(id: string, values: Schema["UpdateResearchSourceRequest"], etag: string) {
    return request<Schema["ResearchSourceResponse"]>(`/research-sources/${id}`, { method: "PATCH", body: values, etag });
  },
  refreshResearchSource(id: string, etag: string) {
    return request<Schema["ResearchSourceResponse"]>(`/research-sources/${id}/refresh`, { method: "POST", etag });
  },
  listResearchSourceVersions(id: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["ResearchSourceVersionResponse"][]>(`/research-sources/${id}/versions${suffix}`);
  },
  getResearchSourceSnapshot(id: string, version: string) {
    return request<Schema["ResearchSourceVersionResponse"]>(`/research-sources/${id}/snapshot?${new URLSearchParams({ version }).toString()}`);
  },
  completeIdeaResearch(id: string, etag: string) {
    return request<Schema["IdeaResponse"]>(`/ideas/${id}/complete-research`, { method: "POST", etag });
  },
  createOriginalFictionalPremise(id: string, values: Schema["CreateOriginalFictionalPremiseRequest"]) {
    return request<Schema["ResearchSourceResponse"]>(`/ideas/${id}/original-fictional-premise`, { method: "POST", body: values });
  },
  listClaims(ideaId: string, filters: { cursor?: string; limit?: number; assessment?: Schema["ClaimAssessmentStatus"] } = {}) {
    const query = new URLSearchParams();
    if (filters.cursor) query.set("cursor", filters.cursor);
    if (filters.limit) query.set("limit", String(filters.limit));
    if (filters.assessment) query.set("assessment", filters.assessment);
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["ClaimResponse"][]>(`/ideas/${ideaId}/claims${suffix}`);
  },
  createClaim(ideaId: string, values: Schema["CreateClaimRequest"]) {
    return request<Schema["ClaimResponse"]>(`/ideas/${ideaId}/claims`, { method: "POST", body: values });
  },
  startClaimExtraction(ideaId: string) {
    return request<Schema["ClaimExtractionResponse"]>(`/ideas/${ideaId}/claims/extractions`, { method: "POST" });
  },
  getClaimExtractionRun(ideaId: string, runId: string) {
    return request<Schema["ClaimExtractionRunResponse"]>(`/ideas/${ideaId}/claims/extractions/${runId}`);
  },
  getClaim(id: string) {
    return request<Schema["ClaimResponse"]>(`/claims/${id}`);
  },
  reviseClaim(id: string, values: Schema["ReviseClaimRequest"], etag: string) {
    return request<Schema["ClaimResponse"]>(`/claims/${id}`, { method: "PATCH", body: values, etag });
  },
  recordClaimDecision(id: string, values: Schema["RecordClaimDecisionRequest"], etag: string) {
    return request<Schema["ClaimResponse"]>(`/claims/${id}/decisions`, { method: "POST", body: values, etag });
  },
  getFactCheckApproval(ideaId: string) {
    return request<Schema["FactCheckReviewReadinessResponse"]>(`/ideas/${ideaId}/fact-check-approval`);
  },
  approveFactCheck(ideaId: string, values: Schema["ApproveFactCheckReviewRequest"], etag: string) {
    return request<Schema["FactCheckReviewReadinessResponse"]>(`/ideas/${ideaId}/fact-check-approval`, { method: "POST", body: values, etag });
  },
  getEvidenceReport(ideaId: string) {
    return request<Schema["EvidenceReportResponse"]>(`/ideas/${ideaId}/evidence-report`);
  },
  listResearchSourceHostPolicies() {
    return request<Schema["ResearchSourceHostPolicyResponse"][]>("/research-source-policies");
  },
  createResearchSourceHostPolicy(values: Schema["CreateResearchSourceHostPolicyRequest"]) {
    return request<Schema["ResearchSourceHostPolicyResponse"]>("/research-source-policies", { method: "POST", body: values });
  },
  updateResearchSourceHostPolicy(id: string, values: Schema["UpdateResearchSourceHostPolicyRequest"], etag: string) {
    return request<Schema["ResearchSourceHostPolicyResponse"]>(`/research-source-policies/${id}`, { method: "PATCH", body: values, etag });
  },
  listIdeas(filters: { channelId?: string; status?: Schema["IdeaStatus"]; tag?: string; origin?: Schema["IdeaOrigin"]; cursor?: string; limit?: number } = {}) {
    const query = new URLSearchParams();
    if (filters.channelId) query.set("channelId", filters.channelId);
    if (filters.status) query.set("status", filters.status);
    if (filters.tag) query.set("tag", filters.tag);
    if (filters.origin) query.set("origin", filters.origin);
    if (filters.cursor) query.set("cursor", filters.cursor);
    if (filters.limit) query.set("limit", String(filters.limit));
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["IdeaResponse"][]>(`/ideas${suffix}`);
  },
  getIdea(id: string) {
    return request<Schema["IdeaResponse"]>(`/ideas/${id}`);
  },
  createIdea(values: Schema["CreateIdeaRequest"]) {
    return request<Schema["IdeaResponse"]>("/ideas", { method: "POST", body: values });
  },
  updateIdea(id: string, values: Schema["UpdateIdeaRequest"], etag: string) {
    return request<Schema["IdeaResponse"]>(`/ideas/${id}`, { method: "PATCH", body: values, etag });
  },
  archiveIdea(id: string, reason: string, etag: string) {
    return request<Schema["IdeaResponse"]>(`/ideas/${id}/archive`, { method: "POST", body: { reason }, etag });
  },
  reconsiderIdea(id: string, reason: string | null, etag: string) {
    return request<Schema["IdeaResponse"]>(`/ideas/${id}/reconsider`, { method: "POST", body: { reason }, etag });
  },
  promoteIdeaToResearch(id: string, etag: string) {
    return request<Schema["IdeaResponse"]>(`/ideas/${id}/promote-to-research`, { method: "POST", etag });
  },
  previewIdeaDeduplication(values: Schema["DeduplicationPreviewRequest"]) {
    return request<Schema["DeduplicationPreviewResponse"]>("/ideas/deduplication-preview", { method: "POST", body: values });
  },
  getIdeaEmbeddingSettings() {
    return request<Schema["IdeaEmbeddingSettingsResponse"]>("/ideas/embedding-settings");
  },
  updateIdeaEmbeddingSettings(values: Schema["UpdateIdeaEmbeddingSettingsRequest"], etag: string) {
    return request<Schema["IdeaEmbeddingSettingsResponse"]>("/ideas/embedding-settings", { method: "PATCH", body: values, etag });
  },
  previewIdeaEmbeddingReindex() {
    return request<Schema["IdeaEmbeddingReindexPreviewResponse"]>("/ideas/embedding-reindexes/preview", { method: "POST" });
  },
  startIdeaEmbeddingReindex(etag: string) {
    return request<Schema["IdeaEmbeddingReindexResponse"]>("/ideas/embedding-reindexes", { method: "POST", body: { confirm: true }, etag });
  },
  listPresets() {
    return request<Schema["PresetResponse"][]>("/presets");
  },
  getPreset(id: string) {
    return request<Schema["PresetResponse"]>(`/presets/${id}`);
  },
  createPreset(values: Schema["CreatePresetCommand"]) {
    return request<Schema["PresetResponse"]>("/presets", { method: "POST", body: values });
  },
  updatePreset(id: string, values: Schema["CreatePresetCommand"], etag: string) {
    return request<Schema["PresetResponse"]>(`/presets/${id}`, { method: "PATCH", body: values, etag });
  },
  listCampaigns() {
    return request<Schema["CampaignResponse"][]>("/campaigns");
  },
  getCampaign(id: string) {
    return request<Schema["CampaignResponse"]>(`/campaigns/${id}`);
  },
  estimateCampaign(values: Schema["EstimateCampaignCommand"]) {
    return request<Schema["CostEstimateResponse"]>("/campaigns/estimate", { method: "POST", body: values, idempotency: false });
  },
  createCampaign(values: Schema["CreateCampaignCommand"]) {
    return request<Schema["CampaignResponse"]>("/campaigns", { method: "POST", body: values });
  },
  runCampaign(id: string, values: RunCampaignRequest, etag: string) {
    return request<Schema["CampaignResponse"]>(`/campaigns/${id}/run`, { method: "POST", body: values, etag });
  },
  getCampaignPlan(id: string) {
    return request<Schema["CampaignPlanResponse"]>(`/campaigns/${id}/plan`);
  },
  listCampaignPlanRevisions(id: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["CampaignPlanResponse"][]>(`/campaigns/${id}/plan/revisions${suffix}`);
  },
  startCampaignPlan(id: string) {
    return request<Schema["CampaignResponse"]>(`/campaigns/${id}/plan`, { method: "POST" });
  },
  createCampaignPlanRegenerationPreview(id: string, values: Schema["PlanRegenerationPreviewRequest"], etag: string) {
    return request<Schema["PlanRegenerationPreviewResponse"]>(`/campaigns/${id}/plan/regeneration-previews`, { method: "POST", body: values, etag });
  },
  regenerateCampaignPlan(id: string, values: Schema["RegenerateCampaignPlanRequest"], etag: string) {
    return request<Schema["CampaignResponse"]>(`/campaigns/${id}/plan/regenerations`, { method: "POST", body: values, etag });
  },
  approveCampaignPlan(id: string, values: Schema["ApproveCampaignPlanRequest"], etag: string) {
    return request<Schema["CampaignPlanResponse"]>(`/campaigns/${id}/plan/approve`, { method: "POST", body: values, etag });
  },
  getStoryboard(outputId: string) {
    return request<StoryboardWireResponse>(`/outputs/${outputId}/storyboard`).then(mapStoryboardResponse);
  },
  listStoryboardRevisions(outputId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<StoryboardRevisionWire[]>(`/outputs/${outputId}/storyboard/revisions${suffix}`).then(response => ({ ...response, data: response.data.map(revision => ({ id: revision.id, number: revision.number, status: revision.status, version: 1, createdAt: revision.createdAt, inputFingerprint: revision.snapshotHash })) }));
  },
  createStoryboardWorkingRevision(outputId: string, etag: string) {
    return request<StoryboardWireResponse>(`/outputs/${outputId}/storyboard/revisions`, { method: "POST", body: { reason: "Storyboard revision requested." }, etag }).then(mapStoryboardResponse);
  },
  patchStoryboardRevision(outputId: string, revisionId: string, values: StoryboardRevisionPatch, etag: string) {
    return request<StoryboardWireResponse>(`/outputs/${outputId}/storyboard/revisions/${revisionId}`, { method: "PATCH", body: storyboardPatch(values), etag }).then(mapStoryboardResponse);
  },
  listStoryboardComments(outputId: string, revisionId: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<StoryboardComment[]>(`/outputs/${outputId}/storyboard/revisions/${revisionId}/comments${suffix}`);
  },
  addStoryboardComment(outputId: string, revisionId: string, values: { body: string; sceneId: string | null }) {
    return request<{ id: string; revisionId: string; logicalSceneId: string | null; actorId: string; body: string; createdAt: string }>(`/outputs/${outputId}/storyboard/revisions/${revisionId}/comments`, { method: "POST", body: { body: values.body, logicalSceneId: values.sceneId } }).then(response => ({ ...response, data: { id: response.data.id, revisionId: response.data.revisionId, sceneId: response.data.logicalSceneId, createdBy: response.data.actorId, body: response.data.body, createdAt: response.data.createdAt } }));
  },
  decideStoryboardReview(outputId: string, revisionId: string, values: StoryboardReviewRequest, etag: string) {
    const decision = values.decision === "Approve" ? "Approved" : values.decision === "Reject" ? "Rejected" : "ChangesRequested";
    return request<StoryboardWireResponse>(`/outputs/${outputId}/storyboard/review-decisions`, { method: "POST", body: { revisionId, decision, reason: values.reason, confirm: values.confirm }, etag }).then(mapStoryboardResponse);
  },
  getStoryboardDiff(outputId: string, revisionId: string, againstRevisionId: string) {
    return request<{ fromRevisionId: string; toRevisionId: string; items: Array<{ path: string; before: string | null; after: string | null }> }>(`/outputs/${outputId}/storyboard/revisions/${revisionId}/diff/${againstRevisionId}`).then(response => ({ ...response, data: { fromRevisionId: response.data.fromRevisionId, toRevisionId: response.data.toRevisionId, summary: response.data.items.map(item => item.path), addedSceneIds: response.data.items.filter(item => item.before === null && item.path.startsWith("scenes/")).map(item => item.path.split("/")[1]), removedSceneIds: response.data.items.filter(item => item.after === null && item.path.startsWith("scenes/")).map(item => item.path.split("/")[1]), changedSceneIds: response.data.items.filter(item => item.before !== null && item.after !== null && item.path.startsWith("scenes/")).map(item => item.path.split("/")[1]) } }));
  },
  previewStoryboardRegeneration(outputId: string, revisionId: string, values: StoryboardRegenerationPreviewRequest, etag: string) {
    void revisionId;
    return request<{ id: string; fingerprint: string; incrementalCostUsd: number | string; expiresAt: string }>(`/outputs/${outputId}/storyboard/regeneration-previews`, { method: "POST", body: { logicalSceneId: values.sceneId, reason: values.reason }, etag }).then(response => ({ ...response, data: { id: response.data.id, fingerprint: response.data.fingerprint, invalidatesApproval: true, estimatedCostDeltaUsd: response.data.incrementalCostUsd, expiresAt: response.data.expiresAt } }));
  },
  startStoryboardRegeneration(outputId: string, revisionId: string, values: { previewId: string; previewFingerprint: string }, etag: string) {
    void revisionId;
    return request<{ operationId: string; revisionId: string }>(`/outputs/${outputId}/storyboard/regenerations`, { method: "POST", body: { ...values, confirm: true }, etag });
  },
  getCampaignPreflight(id: string) {
    return request<Schema["CampaignPreflightResponse"]>(`/campaigns/${id}/preflight`);
  },
  listCampaignPreflightRuns(id: string, cursor?: string) {
    const suffix = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : "";
    return request<Schema["CampaignPreflightResponse"][]>(`/campaigns/${id}/preflight/runs${suffix}`);
  },
  overrideCampaignPreflight(id: string, runId: string, values: Schema["OverrideCampaignPreflightRequest"], etag: string) {
    return request<Schema["CampaignPreflightResponse"]>(`/campaigns/${id}/preflight/${runId}/override`, { method: "POST", body: values, etag });
  },
  cancelCampaign(id: string) {
    return request<Schema["OperationSummaryResponse"]>(`/campaigns/${id}/cancel`, { method: "POST" });
  },
  getOperation(id: string) {
    return request<Schema["OperationDetailsResponse"]>(`/operations/${id}`);
  },
  cancelOperation(id: string) {
    return request<Schema["OperationSummaryResponse"]>(`/operations/${id}/cancel`, { method: "POST" });
  },
  retryOperation(id: string) {
    return request<Schema["OperationSummaryResponse"]>(`/operations/${id}/retry`, { method: "POST" });
  },
  resumeOperation(id: string) {
    return request<Schema["OperationSummaryResponse"]>(`/operations/${id}/resume`, { method: "POST" });
  },
  diagnosticsHealth() {
    return request<Schema["DiagnosticHealth"]>("/diagnostics/health");
  },
  diagnosticsQueues() {
    return request<Schema["QueueDiagnostic"][]>("/diagnostics/queues");
  },
  auditEvents(query: URLSearchParams) {
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<Schema["AuditEventEntry"][]>(`/audit-events${suffix}`);
  },
  securityEvents(query: URLSearchParams) {
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<SecurityEventView[]>(`/security-events${suffix}`);
  },
  downloadOutputVideo(campaignId: string, outputId: string) {
    return download(`/campaigns/${campaignId}/outputs/${outputId}/video`);
  },
  outputVideoUrl(campaignId: string, outputId: string): string {
    return `/api/v1/campaigns/${campaignId}/outputs/${outputId}/video`;
  }
};
